// The rules behind the Intertown trip screen, kept free of React and Firebase so they can be
// tested on their own: which part of the trip a status is, what can still be cancelled, the
// arrival time, the waiting charge so far, and what gets shared.

import { liveLocationMaxAgeMs, locationFreshness } from './intertownDisplay';

export const FINDING_STATUSES = ['pending_admin_confirmation', 'open_for_claim', 'awaiting_driver_acceptance'];

// Exactly the states the server lets a rider cancel in (cancelIntertownRequestSecure in
// firebase/functions/intertown.js): not once Anna has arrived at the pickup.
export const CANCELLABLE_STATUSES = new Set([...FINDING_STATUSES, 'confirmed']);

// States where Anna's phone is sending a location the rider can follow.
export const LIVE_STATUSES = new Set(['confirmed', 'arrived_at_pickup', 'started', 'at_destination', 'returning']);

// Legs where Anna is driving, so a quiet location is worth a warning. (Parked at the pickup or
// the destination, a phone legitimately reports rarely.)
export const MOVING_STATUSES = new Set(['confirmed', 'started', 'returning']);

const own = (table, key) => (Object.prototype.hasOwnProperty.call(table, key) ? table[key] : undefined);
const num = (value) => (value === null || value === undefined || value === '' ? NaN : Number(value));
const asPoint = (place) => (Number.isFinite(num(place?.lat)) && Number.isFinite(num(place?.lng))
  ? { lat: Number(place.lat), lng: Number(place.lng) }
  : null);
export const timestampMs = (value) => {
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (Number(value?._seconds)) return Number(value._seconds) * 1000;
  if (Number(value?.seconds)) return Number(value.seconds) * 1000;
  return Number(value) || 0;
};

const haversineKm = (a, b) => {
  const rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
};

/** Which part of the trip a request status is, for the screen to show. Null for anything finished. */
export function tripPhase(status) {
  if (FINDING_STATUSES.includes(status)) return 'finding';
  return own({
    confirmed: 'to_pickup',
    arrived_at_pickup: 'at_pickup',
    started: 'on_trip',
    at_destination: 'waiting',
    returning: 'returning',
  }, status) || null;
}

export const canCancelTrip = (status) => CANCELLABLE_STATUSES.has(status);

/** The three steps shown before the trip starts: Finding Anna, On the way, At pickup. */
export function pickupSteps(phase) {
  const order = ['finding', 'to_pickup', 'at_pickup'];
  const at = order.indexOf(phase);
  if (at < 0) return [];
  return order.map((key, i) => ({ key, state: i < at ? 'done' : i === at ? 'current' : 'todo' }));
}

/**
 * The short status chip beside the route at the top of the screen: its label key and its tone.
 * An unresponsive Anna turns a moving leg amber without changing what the trip is doing.
 */
export function statusChip(status, { delayed = false } = {}) {
  const base = own({
    pending_admin_confirmation: ['finding', 'neutral'],
    open_for_claim: ['finding', 'neutral'],
    awaiting_driver_acceptance: ['finding', 'neutral'],
    confirmed: ['onTheWay', 'good'],
    arrived_at_pickup: ['atPickup', 'good'],
    started: ['inProgress', 'good'],
    at_destination: ['waiting', 'info'],
    returning: ['returning', 'good'],
  }, status);
  if (!base) return null;
  return { key: base[0], tone: delayed && MOVING_STATUSES.has(status) ? 'warn' : base[1] };
}

/**
 * How long until Anna reaches the end of the current leg, in minutes, or null when it cannot be
 * said honestly. Driving: the minutes the rider was shown at booking, scaled by how much of the
 * straight line is still ahead (it starts at exactly the booked time and counts down). Going to
 * the pickup: the server's estimate, but only if it is for that leg.
 */
export function tripEtaMin({ trip, driverLocation, freshness }) {
  if (freshness !== 'live' || !driverLocation) return null;
  const status = trip?.status;
  if (status === 'confirmed') {
    const eta = num(driverLocation.etaMin);
    const phase = driverLocation.etaPhase;
    return Number.isFinite(eta) && eta >= 0 && (phase === undefined || phase === null || phase === 'pickup') ? eta : null;
  }
  if (status !== 'started' && status !== 'returning') return null;
  const planned = num(trip?.route?.durationMin);
  const pickup = asPoint(trip?.pickup);
  const destination = asPoint(trip?.destination || trip?.route?.destination);
  const here = asPoint(driverLocation);
  if (!(planned > 0) || !pickup || !destination || !here) return null;
  const tripKm = haversineKm(pickup, destination);
  if (!(tripKm >= 0.5)) return null;
  const remainingKm = haversineKm(here, status === 'returning' ? pickup : destination);
  return planned * Math.min(1.5, remainingKm / tripKm);
}

/** Minutes split for display: { hours, minutes } rounded to a whole minute. */
export function etaParts(minutes) {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  return { hours: Math.floor(total / 60), minutes: total % 60, total };
}

/** "About 1 hr 20 min", "About 45 min", "Arriving now": the time left as the big line on the screen. */
export function etaHeadline(t, minutes) {
  const { hours, minutes: m, total } = etaParts(minutes);
  if (total < 1) return t('intertown.trip.etaNow');
  if (hours > 0 && m > 0) return t('intertown.trip.etaHrMin', { h: hours, m });
  if (hours > 0) return t('intertown.trip.etaHr', { h: hours });
  return t('intertown.trip.etaMin', { m });
}

/** The clock time Anna is expected, like "11:35 PM", in the rider's language. */
export function arrivalClock(nowMs, minutes, lang = 'en') {
  const at = new Date(Number(nowMs) + Math.max(0, Number(minutes) || 0) * 60000);
  if (Number.isNaN(at.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(lang === 'te' ? 'te-IN' : 'en-IN', { hour: 'numeric', minute: '2-digit', hour12: true })
      .format(at)
      .replace(/\b(am|pm)\b/i, (m) => m.toUpperCase());
  } catch {
    return `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;
  }
}

/** A running clock: 06:12, or 1:35:12 once past an hour. */
export function clockDuration(milliseconds) {
  const seconds = Math.max(0, Math.floor(Number(milliseconds) / 1000) || 0);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const two = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${two(m)}:${two(s)}` : `${two(m)}:${two(s)}`;
}

/**
 * The waiting charge so far at the destination of a return trip. The same formula the server
 * applies when Anna starts the return leg (updateIntertownTripSecure, action start_return): whole
 * minutes rounded up, capped at the maximum, free minutes first, then per minute or per hour
 * (hours rounded up). The server's number is final; this one is what the clock has reached.
 */
export function waitingSoFar({ policy, startedMs, nowMs }) {
  const p = policy || {};
  const started = Number(startedMs) || nowMs;
  const waitedMinutes = Math.min(Number(p.maxMinutes) || 0, Math.max(0, Math.ceil((nowMs - started) / 60000)));
  const freeMinutes = Number(p.freeMinutes) || 0;
  const billable = Math.max(0, waitedMinutes - freeMinutes);
  const divisor = p.unit === 'hour' ? 60 : 1;
  const charge = Math.ceil(billable / divisor) * (Number(p.rate) || 0);
  const elapsedMs = Math.max(0, nowMs - started);
  return {
    waitedMinutes,
    // Minutes begun so far, not capped: what "N min waiting" says, so that it agrees with the
    // charge (a minute that has started is a minute the charge counts).
    minutesStarted: Math.ceil(elapsedMs / 60000),
    freeMinutes,
    charge,
    inFreeTime: waitedMinutes <= freeMinutes,
    elapsedMs,
  };
}

/** Whole minutes since Anna's last location, for "Last update 3 min ago". */
export function minutesSince(updatedAt, nowMs) {
  const at = Number(updatedAt);
  if (!Number.isFinite(at) || at <= 0) return null;
  return Math.max(0, Math.floor((nowMs - at) / 60000));
}

/** Freshness of Anna's location for this trip, with the same tolerance the rest of Intertown uses. */
export function tripFreshness({ driverLocation, nowMs, captainLocationMaxAgeSec }) {
  return locationFreshness({
    updatedAt: driverLocation?.updatedAt,
    now: nowMs,
    maxAgeMs: liveLocationMaxAgeMs(captainLocationMaxAgeSec),
  });
}

/**
 * The two ends of the trip as short names, and "From → To" for the top of the screen. The names
 * the server stored on the request come first; the pin's own name is only a fallback.
 */
export function tripPlaceNames(trip) {
  const short = (place) => String(place?.name || place?.address || '').split(',')[0].trim();
  const from = String(trip?.route?.fromName || '').trim() || short(trip?.pickup);
  const to = String(trip?.route?.toName || '').trim() || short(trip?.destination);
  return { from, to, text: from && to ? `${from} → ${to}` : from || to };
}

/**
 * Everything the trip screen derives from the request and Anna's last location, in one place:
 * the part of the trip, the chip, how fresh the location is, the time left, the waiting charge so
 * far, and whether the rider may still cancel. The screen only adds what it owns (the clock and
 * the buttons).
 */
export function tripViewState({ trip, driverLocation, nowMs, captainLocationMaxAgeSec }) {
  const status = trip?.status;
  const freshness = tripFreshness({ driverLocation, nowMs, captainLocationMaxAgeSec });
  const delayed = MOVING_STATUSES.has(status) && freshness === 'stale';
  const names = tripPlaceNames(trip);
  return {
    phase: tripPhase(status),
    chip: statusChip(status, { delayed }),
    freshness,
    delayed,
    eta: tripEtaMin({ trip, driverLocation, freshness }),
    lastUpdateMin: minutesSince(driverLocation?.updatedAt, nowMs),
    waiting: waitingSoFar({ policy: trip?.waitingPolicy, startedMs: trip?.waitingStartedAtMs, nowMs }),
    canCancel: canCancelTrip(status),
    routeText: names.text,
    pickupName: names.from,
    destinationName: names.to,
  };
}

/**
 * The tel: link for Anna's own number, or '' when the request has no usable one. It must never
 * fall back to the MyTownly support number (buildTelUrl does that when given nothing), or "Call
 * Anna" would ring the office.
 */
export function annaTelUrl(rawPhone) {
  const digits = String(rawPhone || '').replace(/\D/g, '').slice(-10);
  return digits.length === 10 ? `tel:+91${digits}` : '';
}

/**
 * The plain-text trip details a rider can send to family: driver, plate and route, and (when the
 * server made one) the live link to follow the trip. No PIN, no phone numbers. Only an https
 * link is added: anything else is left out rather than sent to family.
 */
export function tripShareText({ trip, routeText, driverName, link }) {
  const lines = ['MyTownly Rides Intertown trip'];
  if (routeText) lines.push(routeText);
  if (driverName) lines.push(`Driver: ${driverName}${trip?.vehicleNumber ? ` · ${trip.vehicleNumber}` : ''}`);
  else if (trip?.vehicleNumber) lines.push(`Vehicle: ${trip.vehicleNumber}`);
  const started = timestampMs(trip?.startedAt);
  if (started) {
    try {
      lines.push(`Started: ${new Intl.DateTimeFormat('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }).format(new Date(started)).toUpperCase()}`);
    } catch { /* leave the time out */ }
  }
  if (typeof link === 'string' && /^https:\/\/\S+$/.test(link)) lines.push(`Track live: ${link}`);
  return lines.join('\n');
}
